/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  BellAlertIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { monitoringService, MonitorAlertRule } from '../../services/registryApi';
import toast from 'react-hot-toast';

/* ── Constants ────────────────────────────────────────────────────────── */

const METRIC_KEYS = [
  { label: 'P99 Latency', value: 'p99_latency' },
  { label: 'P95 Latency', value: 'p95_latency' },
  { label: 'Avg Latency', value: 'avg_latency' },
  { label: 'Error Rate', value: 'error_rate' },
  { label: 'Throughput', value: 'throughput' },
  { label: 'Accuracy', value: 'accuracy' },
];

const CONDITIONS = [
  { label: '> (greater than)', value: 'gt' },
  { label: '< (less than)', value: 'lt' },
  { label: '>= (greater or equal)', value: 'gte' },
  { label: '<= (less or equal)', value: 'lte' },
];

const WINDOW_SIZES = [
  { label: '5 min', value: '5m' },
  { label: '15 min', value: '15m' },
  { label: '1 hour', value: '1h' },
];

const AUTO_ACTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Trigger Pipeline', value: 'trigger_pipeline' },
  { label: 'Rollback', value: 'rollback' },
];

/* ── Status badge ─────────────────────────────────────────────────────── */

const alertStatusColors: Record<string, string> = {
  active: 'bg-green-500/20 text-green-400',
  triggered: 'bg-red-500/20 text-red-400',
  resolved: 'bg-blue-500/20 text-blue-400',
  disabled: 'bg-gray-700 text-gray-400',
};

const AlertStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2 py-0.5 text-xs font-medium rounded ${alertStatusColors[status] || alertStatusColors.disabled}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

/* ── Condition display ────────────────────────────────────────────────── */

const conditionLabel: Record<string, string> = {
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
};

/* ── Form types ───────────────────────────────────────────────────────── */

interface AlertForm {
  metric_key: string;
  condition: string;
  threshold: number;
  window_size: string;
  notify_email: string;
  notify_webhook: string;
  auto_action: string;
  pipeline_id: string;
}

const emptyForm: AlertForm = {
  metric_key: 'p99_latency',
  condition: 'gt',
  threshold: 500,
  window_size: '5m',
  notify_email: '',
  notify_webhook: '',
  auto_action: 'none',
  pipeline_id: '',
};

/* ── Alert Modal ──────────────────────────────────────────────────────── */

const AlertModal: React.FC<{
  title: string;
  initialForm: AlertForm;
  onClose: () => void;
  onSubmit: (form: AlertForm) => void;
  submitting: boolean;
}> = ({ title, initialForm, onClose, onSubmit, submitting }) => {
  const [form, setForm] = useState<AlertForm>(initialForm);

  const set = (key: keyof AlertForm, value: string | number) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    if (!form.metric_key || !form.condition) {
      toast.error('Metric and condition are required');
      return;
    }
    if (form.auto_action === 'trigger_pipeline' && !form.pipeline_id) {
      toast.error('Pipeline ID is required for trigger_pipeline action');
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Metric + Condition row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Metric *</label>
              <select
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={form.metric_key}
                onChange={(e) => set('metric_key', e.target.value)}
              >
                {METRIC_KEYS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Condition *</label>
              <select
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={form.condition}
                onChange={(e) => set('condition', e.target.value)}
              >
                {CONDITIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Threshold + Window */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Threshold *</label>
              <input
                type="number"
                step="any"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={form.threshold}
                onChange={(e) => set('threshold', Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Window Size</label>
              <select
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={form.window_size}
                onChange={(e) => set('window_size', e.target.value)}
              >
                {WINDOW_SIZES.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notifications */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Notify Email (optional)</label>
            <input
              type="email"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="team@example.com"
              value={form.notify_email}
              onChange={(e) => set('notify_email', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Notify Webhook (optional)</label>
            <input
              type="url"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="https://hooks.slack.com/..."
              value={form.notify_webhook}
              onChange={(e) => set('notify_webhook', e.target.value)}
            />
          </div>

          {/* Auto-action */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Auto Action</label>
            <select
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={form.auto_action}
              onChange={(e) => set('auto_action', e.target.value)}
            >
              {AUTO_ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>

          {/* Pipeline ID (conditional) */}
          {form.auto_action === 'trigger_pipeline' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Pipeline ID *</label>
              <input
                type="text"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="pipeline-uuid"
                value={form.pipeline_id}
                onChange={(e) => set('pipeline_id', e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {submitting ? 'Saving...' : 'Save Alert'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ───────────────────────────────────────────────────── */

const MonitorAlerts: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState<MonitorAlertRule | null>(null);

  /* Fetch monitor info for header */
  const { data: monitorData } = useQuery({
    queryKey: ['monitor', id],
    queryFn: () => monitoringService.get(id!),
    enabled: !!id,
  });

  const monitorName = monitorData?.data?.monitor?.name ?? 'Monitor';

  /* Fetch alert rules */
  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['monitor-alerts', id],
    queryFn: () => monitoringService.listAlerts(id!),
    enabled: !!id,
  });

  const alerts: MonitorAlertRule[] = alertsData?.data?.alerts ?? [];

  /* Create alert mutation */
  const createMutation = useMutation({
    mutationFn: (form: AlertForm) =>
      monitoringService.createAlert(id!, {
        metric_key: form.metric_key,
        condition: form.condition,
        threshold: form.threshold,
        window_size: form.window_size,
        notify_email: form.notify_email || undefined,
        notify_webhook: form.notify_webhook || undefined,
        auto_action: form.auto_action !== 'none' ? form.auto_action : undefined,
        auto_action_config:
          form.auto_action === 'trigger_pipeline' && form.pipeline_id
            ? { pipeline_id: form.pipeline_id }
            : undefined,
      }),
    onSuccess: () => {
      toast.success('Alert rule created');
      queryClient.invalidateQueries({ queryKey: ['monitor-alerts', id] });
      setShowCreateModal(false);
    },
    onError: () => {
      toast.error('Failed to create alert rule');
    },
  });

  /* Update alert mutation */
  const updateMutation = useMutation({
    mutationFn: ({ alertId, form }: { alertId: string; form: AlertForm }) =>
      monitoringService.updateAlert(alertId, {
        metric_key: form.metric_key,
        condition: form.condition as MonitorAlertRule['condition'],
        threshold: form.threshold,
        window_size: form.window_size,
        notify_email: form.notify_email || '',
        notify_webhook: form.notify_webhook || '',
        auto_action: form.auto_action !== 'none' ? form.auto_action : '',
        auto_action_config:
          form.auto_action === 'trigger_pipeline' && form.pipeline_id
            ? JSON.stringify({ pipeline_id: form.pipeline_id })
            : '',
      }),
    onSuccess: () => {
      toast.success('Alert rule updated');
      queryClient.invalidateQueries({ queryKey: ['monitor-alerts', id] });
      setEditingAlert(null);
    },
    onError: () => {
      toast.error('Failed to update alert rule');
    },
  });

  /* Delete alert mutation */
  const deleteMutation = useMutation({
    mutationFn: (alertId: string) => monitoringService.deleteAlert(alertId),
    onSuccess: () => {
      toast.success('Alert rule deleted');
      queryClient.invalidateQueries({ queryKey: ['monitor-alerts', id] });
    },
    onError: () => {
      toast.error('Failed to delete alert rule');
    },
  });

  const handleDelete = (alertId: string) => {
    if (!window.confirm('Delete this alert rule? This cannot be undone.')) return;
    deleteMutation.mutate(alertId);
  };

  /* Parse auto_action_config for editing */
  const parseAutoActionConfig = (configStr: string): string => {
    if (!configStr) return '';
    try {
      const obj = typeof configStr === 'string' ? JSON.parse(configStr) : configStr;
      return obj?.pipeline_id ?? '';
    } catch {
      return '';
    }
  };

  const alertToForm = (alert: MonitorAlertRule): AlertForm => ({
    metric_key: alert.metric_key,
    condition: alert.condition,
    threshold: alert.threshold,
    window_size: alert.window_size,
    notify_email: alert.notify_email || '',
    notify_webhook: alert.notify_webhook || '',
    auto_action: alert.auto_action || 'none',
    pipeline_id: parseAutoActionConfig(alert.auto_action_config),
  });

  /* Format metric key for display */
  const fmtMetricKey = (key: string): string => {
    const found = METRIC_KEYS.find((m) => m.value === key);
    return found?.label ?? key;
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/registry/monitoring/${id}`)}
              className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <BellAlertIcon className="w-7 h-7 text-orange-500" />
                <h1 className="text-2xl font-bold text-white">Alert Rules</h1>
              </div>
              <p className="text-gray-400 mt-1 text-sm ml-10">
                for <span className="text-white font-medium">{monitorName}</span>
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add Alert
          </button>
        </div>

        {/* Alert rules table */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-16 text-center">
            <BellAlertIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
            <h3 className="text-sm font-medium text-white mb-1">No alert rules</h3>
            <p className="text-sm text-gray-400 mb-4">
              Create alert rules to get notified when model metrics exceed your thresholds.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Alert
            </button>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase">Metric</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase">Condition</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase">Threshold</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase">Window</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase">Last Triggered</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {alerts.map((alert) => (
                  <tr key={alert.id} className="hover:bg-gray-800/50">
                    <td className="px-5 py-4 text-sm text-white">
                      {fmtMetricKey(alert.metric_key)}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-300 font-mono">
                      {conditionLabel[alert.condition] || alert.condition}
                    </td>
                    <td className="px-5 py-4 text-sm text-white font-mono">
                      {alert.threshold}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-300">
                      {alert.window_size}
                    </td>
                    <td className="px-5 py-4">
                      <AlertStatusBadge status={alert.status} />
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-400">
                      {alert.last_triggered_at
                        ? new Date(alert.last_triggered_at).toLocaleString()
                        : '--'}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingAlert(alert)}
                          className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
                          title="Edit"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(alert.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create modal */}
        {showCreateModal && (
          <AlertModal
            title="Add Alert Rule"
            initialForm={emptyForm}
            onClose={() => setShowCreateModal(false)}
            onSubmit={(form) => createMutation.mutate(form)}
            submitting={createMutation.isPending}
          />
        )}

        {/* Edit modal */}
        {editingAlert && (
          <AlertModal
            title="Edit Alert Rule"
            initialForm={alertToForm(editingAlert)}
            onClose={() => setEditingAlert(null)}
            onSubmit={(form) =>
              updateMutation.mutate({ alertId: editingAlert.id, form })
            }
            submitting={updateMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};

export default MonitorAlerts;
